import axios, { AxiosResponse } from 'axios';

export /**
 * @method get
 * @brief 마이페이지 정보조회 API
 */
const getMyPageInfoAPI = async (centerId: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'get',
            url: `${process.env.REACT_APP_SERVER}/v2/centers/my/${centerId}/`,
            headers: { authorization: accessToken },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류.');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 마이페이지 대표자 정보 수정 API
 */
const modifyRepresentativeInformationAPI = async (
    adminId: string,
    name: string,
    phoneNumber: string,
    gender: string,
    email: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/my/${adminId}/`,
            headers: { authorization: accessToken },
            data: {
                name,
                phoneNumber,
                gender,
                email,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 마이페이지 센터 정보 수정 API
 */
const modifyCenterInformationAPI = async (
    centerId: string,
    centerName: string,
    centerCategory: string,
    centerAddress: string,
    accessToken: string
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/centers/my/${centerId}/`,
            headers: { authorization: accessToken },
            data: {
                centerName,
                centerCategory,
                centerAddress,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method post
 * @brief 마이페이지 프로필사진 수정 API
 */
const modifyProfileImageAPI = async (adminId: string, imgBase64: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/images/admin-profile/${adminId}/`,
            headers: { authorization: accessToken },
            data: {
                imgBase64,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        const status = error.response?.status;
        switch (status) {
            case 400:
                return { status: 400, data: error.response?.data };
            case 401:
                return { status: 401 };
            case 403:
                return { status: 403 };
            case 404:
                return { status: 404 };
            case 500:
                return { status: 500 };
            default:
                return { status: status ?? 500 };
        }
    }
    return response;
};

export /**
 * @method post
 * @brief 마이페이지 비밀번호 변경전 확인
 */
const modifyPasswordPrevCheckAPI = async (adminId: string, password: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'post',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/check-password/${adminId}/`,
            headers: { authorization: accessToken },
            data: {
                password,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 418:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 마이페이지 비밀번호 변경
 */
/**
 * ⚠️ **현재 비밀번호(`password`)를 반드시 함께 보낸다.**
 *
 * 예전에는 `newPassword` 만 보냈다. 화면은 현재 비밀번호를 입력받아 앞에서 따로 확인했지만
 * (`modifyPasswordPrevCheckAPI`) **그건 클라이언트 단속일 뿐**이라, 서버는 유효한 관리자
 * 토큰만 있으면 현재 비밀번호를 몰라도 바꿔 줬다. 토큰이 새면 공격자가 먼저 비밀번호를 바꿔
 * **원래 대표를 잠근다** — "비밀번호를 바꾸면 복구된다"는 대응책이 거꾸로 뒤집힌다.
 *
 * 서버는 **보내오면 검증하고, 틀리면 418** 을 준다(아직 필수는 아니다 — 이 웹이 배포된 뒤
 * 백엔드에서 필수로 올린다).
 *
 * 🔴 배포 순서: **백엔드(password 선택 허용) → 이 웹 → 백엔드(password 필수).**
 * 구 백엔드는 Joi 가 닫혀 있어 `password` 를 모르는 키로 보고 400 을 준다 — 이 웹을 먼저
 * 내보내면 비밀번호 변경이 막힌다.
 */
const modifyPasswordAPI = async (
    adminId: string,
    newPassword: string,
    accessToken: string,
    currentPassword: string,
): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/change-password/${adminId}`,
            headers: { authorization: accessToken },
            data: {
                password: currentPassword,
                newPassword,
            },
        });

        if (response.status === 200) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};

export /**
 * @method patch
 * @brief 마이페이지 회원탈퇴
 */
const deleteAccountAPI = async (adminId: string, reason: string, accessToken: string): Promise<any> => {
    let response = {} as AxiosResponse<any, any>;
    try {
        response = await axios({
            method: 'patch',
            url: `${process.env.REACT_APP_SERVER}/v1/admins/withdraw/${adminId}`,
            headers: { authorization: accessToken },
            data: {
                reason,
            },
        });

        if (response.status === 204) {
            return response;
        }
    } catch (error: any) {
        // Error 😨
        switch (error.response.status) {
            case 400:
                return { status: error.response.status };
            case 401:
                return { status: error.response.status };
            case 403:
                return { status: error.response.status };
            case 404:
                return { status: error.response.status };
            case 406:
                return { status: error.response.status };
            case 500:
                alert('서버 내부 오류');
                return { status: error.response.status };
        }
    }
};
